import { motion } from "framer-motion";
import { BrainCircuit, Zap, ShieldCheck, RefreshCw, Workflow, Layers } from "lucide-react";

const FEATURES = [
  { icon: BrainCircuit, title: "AI-Powered Analysis", copy: "Advanced deep learning models analyze X-rays, CT scans, and MRIs with high accuracy." },
  { icon: Zap, title: "Rapid Processing", copy: "Get preliminary diagnostic insights in minutes, not hours." },
  { icon: ShieldCheck, title: "Enterprise Security", copy: "HIPAA-compliant infrastructure with PHI protection and BAA support." },
  { icon: RefreshCw, title: "Continuous Learning", copy: "Models improve over time with radiologist feedback and validation." },
  { icon: Workflow, title: "Collaborative Workflow", copy: "Seamless integration with existing radiology workflows and PACS systems." },
  { icon: Layers, title: "Multi-Modal Support", copy: "Support for X-Ray, CT, MRI, and Ultrasound imaging modalities." },
];

export default function PlatformFeatures() {
  return (
    <section id="platform" className="bg-ink py-24 md:py-32 border-t border-slate-200" data-testid="platform-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6"
          >
            The platform
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]"
          >
            Comprehensive AI
            <br />
            <span className="text-mute">imaging platform.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-mute text-base leading-relaxed mt-6"
          >
            Built for radiologists, designed for accuracy, engineered for compliance.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="group bg-ink p-8 md:p-10 hover:bg-surface transition-colors duration-500"
              data-testid={`platform-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="w-11 h-11 border border-slate-200 flex items-center justify-center mb-8 group-hover:border-brand group-hover:bg-brand/10 transition-colors duration-300">
                <f.icon className="w-5 h-5 text-brand" />
              </span>
              <h3 className="font-display text-xl font-semibold tracking-tight mb-3">{f.title}</h3>
              <p className="text-mute text-sm leading-relaxed">{f.copy}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
